import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  getLeadsApi,
  getLeadByIdApi,
  updateLeadByIdApi,
  updateGradeApi,
  addCallLogApi,
} from "../../api/leads.api";
import { addPaymentApi, deletePaymentApi } from "../../api/payments.api";
import {
  addAttachmentApi,
  deleteAttachmentApi,
} from "../../api/attachment.api";
import { useSettings } from "../../context/SettingsContext";
import {
  addBookingApi,
  updateBookingApi,
  deleteBookingApi,
} from "../../api/bookings.api";
import { addTaskApi, updateTaskApi, deleteTaskApi } from "../../api/tasks.api";
import { createLeadApi } from "../../api/leads.api";
import socket from "../../utils/socket";

export default function LeadsLayout() {
  const settings = useSettings();
  const location = useLocation();

  const [leads, setLeads] = useState([]);
  const [activeLead, setActiveLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);
  const [isStale, setIsStale] = useState(false);
  const [fetchComplete, setFetchComplete] = useState(false);

  const clearActiveLead = useCallback(() => {
    setActiveLead(null);
  }, []);

  const slotToMinutes = (slot) => {
    if (!slot) return null;
    const [s, e] = slot.split("–").map((v) => v.trim());
    const [sh, sm] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    return { start: sh * 60 + sm, end: eh * 60 + em };
  };

  const hasGlobalBookingConflict = ({
    leads,
    type,
    dateISO,
    slot,
    ignoreBookingId,
  }) => {
    if (!dateISO || !slot) return false;
    const date = dateISO.slice(0, 10);
    const target = slotToMinutes(slot);
    if (!target) return false;

    return leads.some((lead) =>
      (lead.bookings || []).some((b) => {
        if (b._id === ignoreBookingId) return false;
        if (b.type !== type) return false;
        if (b.status === "Cancelled") return false;

        const bDate = b.date?.slice(0, 10);
        if (bDate !== date) return false;

        const range = slotToMinutes(b.slot);
        if (!range) return false;

        return target.start < range.end && target.end > range.start;
      }),
    );
  };

  const isLeadsPage = location.pathname.startsWith("/leads");
  const isPipelinesPage = location.pathname.startsWith("/pipelines");

  // Normalize socket lead to match batch-loaded format
  const normalizeBatch = useCallback((raw) => {
    const ae = raw.assign_employee;
    return {
      ...raw,
      source: raw.source || raw.custom_fields?.lead_source || null,
      assign_employee:
        ae && typeof ae === "object"
          ? { id: ae._id || ae.id, name: ae.name, team: ae.team }
          : ae
            ? { id: ae }
            : null,
    };
  }, []);

  // Real-time lead listeners — exact same pattern as NotificationDropdown
  useEffect(() => {
    const normalize = (raw) => {
      const ae = raw.assign_employee;
      return {
        ...raw,
        source: raw.source || raw.custom_fields?.lead_source || null,
        assign_employee:
          ae && typeof ae === "object"
            ? { id: ae._id || ae.id, name: ae.name, team: ae.team }
            : ae
              ? { id: ae }
              : null,
      };
    };

    const onCreated = (raw) => {
      const lead = normalize(raw);
      setLeads((prev) => {
        if (prev.some((l) => l._id === lead._id)) return prev;
        return [lead, ...prev];
      });
    };

    const onUpdated = (raw) => {
      const lead = normalize(raw);
      setLeads((prev) => prev.map((l) => (l._id === lead._id ? lead : l)));
      setActiveLead((prev) => {
        if (!prev || prev._id !== lead._id) return prev;
        return lead;
      });
    };

    // Controller io.emit events (same mechanism as notification:new)
    socket.on("lead-created", onCreated);
    socket.on("lead-updated", onUpdated);
    socket.on("lead-status-updated", onUpdated);
    // Relay events (client → server → other clients)
    socket.on("lead:created", onCreated);
    socket.on("lead:updated", onUpdated);
    socket.on("lead:status-updated", onUpdated);

    // Followup events — keep leads array in sync when followups change from any page
    const onFollowupRescheduled = (data) => {
      if (!data?.leadId) return;
      setLeads((prev) =>
        prev.map((l) =>
          l._id === data.leadId
            ? { ...l, next_followup: data.newDate, followup_status: "rescheduled" }
            : l
        )
      );
    };

    const onFollowupCompleted = (data) => {
      if (!data?.leadId) return;
      setLeads((prev) =>
        prev.map((l) =>
          l._id === data.leadId
            ? { ...l, followup_status: "completed" }
            : l
        )
      );
    };

    const onFollowupTaskCreated = (data) => {
      if (!data?.leadId) return;
      setLeads((prev) =>
        prev.map((l) =>
          l._id === data.leadId
            ? { ...l, next_followup: data.nextFollowup, followup_status: "pending" }
            : l
        )
      );
    };

    socket.on("lead-followup-rescheduled", onFollowupRescheduled);
    socket.on("lead-followup-completed", onFollowupCompleted);
    socket.on("lead-followup-task-created", onFollowupTaskCreated);

    return () => {
      socket.off("lead-created", onCreated);
      socket.off("lead-updated", onUpdated);
      socket.off("lead-status-updated", onUpdated);
      socket.off("lead:created", onCreated);
      socket.off("lead:updated", onUpdated);
      socket.off("lead:status-updated", onUpdated);
      socket.off("lead-followup-rescheduled", onFollowupRescheduled);
      socket.off("lead-followup-completed", onFollowupCompleted);
      socket.off("lead-followup-task-created", onFollowupTaskCreated);
    };
  }, []);

  const fetchLeads = useCallback(() => {
    if (fetchedRef.current) return;

    fetchedRef.current = true;
    setFetchComplete(false);
    setLoading(true);
    setLeads([]);

    socket.off("leads-batch");
    socket.off("leads-complete");
    socket.off("leads-error");

    const emitFetch = () => {
      socket.emit("get-leads-batch", { batchSize: 100 });
    };

    if (socket.connected) {
      emitFetch();
    } else {
      socket.connect();
      socket.once("connect", emitFetch);
    }

    socket.on("leads-batch", (batch) => {
      setLeads((prev) => {
        const next = [...prev, ...batch];
        if (next.length > 0) setLoading(false);
        return next;
      });
    });

    socket.once("leads-complete", () => {
      setLoading(false);
      setFetchComplete(true);
      fetchedRef.current = false;

      socket.off("leads-batch");
      socket.off("leads-error");
    });

    socket.once("leads-error", () => {
      setLoading(false);
      setFetchComplete(true);
      fetchedRef.current = false;
    });
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    return () => {
      socket.off("leads-batch");
      socket.off("leads-complete");
      socket.off("leads-error");
      fetchedRef.current = false;
      // Do NOT disconnect the shared socket — other components
      // (NotificationDropdown) rely on it staying connected
    };
  }, []);

  useEffect(() => {
    if (!isStale) return;
    if (
      !location.pathname.startsWith("/leads") &&
      !location.pathname.startsWith("/pipelines")
    )
      return;

    fetchedRef.current = false;
    setIsStale(false);
    fetchLeads();
  }, [isStale, location.pathname, fetchLeads]);

  const loadLeadById = useCallback(async (id) => {
    setActiveLead(null);
    const res = await getLeadByIdApi(id);
    setActiveLead(res.data.data);
  }, []);

  const touch = () => new Date().toISOString();

  const updateLeadGlobal = useCallback((updater, leadId) => {
    setLeads((prev) =>
      prev.map((l) => {
        if (l._id !== leadId) return l;

        const next = typeof updater === "function" ? updater(l) : updater;
        if (!next) return l;

        return {
          ...l,
          ...next,
          updatedAt: touch(),
        };
      }),
    );

    setActiveLead((prev) => {
      if (!prev || prev._id !== leadId) return prev;

      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!next) return prev;

      return {
        ...prev,
        ...next,
        updatedAt: touch(),
      };
    });
  }, []);

  const updateLeadField = useCallback(
    async (id, key, val, pipelineKeys = []) => {
      if (key === "current_status") {
        const res = await updateGradeApi(id, {
          key: val.key,
          type: pipelineKeys.includes(val.key)
            ? "pipeline"
            : val.type || "grade",
        });
        updateLeadGlobal(res.data.data, id);
        socket.emit("lead:status-updated", res.data.data);
        return;
      }

      if (key === "call_logs") {
        const res = await addCallLogApi(id, val);

        updateLeadGlobal(res.data.data, res.data.data._id);
        socket.emit("lead:updated", res.data.data);
        setIsStale(true);
        return;
      }

      const payload =
        key === "custom_fields" ? { custom_fields: val } : { [key]: val };

      const res = await updateLeadByIdApi(id, payload);
      updateLeadGlobal(res.data.data, id);
      socket.emit("lead:updated", res.data.data);
    },
    [updateLeadGlobal],
  );

  const createLead = useCallback(async (payload) => {
    const res = await createLeadApi(payload);

    const newLead = normalizeBatch(res.data.data);

    setLeads((prev) => {
      if (prev.some((l) => l._id === newLead._id)) return prev;
      return [newLead, ...prev];
    });
    setActiveLead(newLead);

    // Broadcast to other connected clients via socket relay
    socket.emit("lead:created", newLead);

    return newLead;
  }, [normalizeBatch]);

  const addBooking = useCallback(
    async (leadId, type, payload) => {
      // Slot conflict check removed - multiple bookings per slot now allowed
      const res = await addBookingApi(leadId, type, payload);
      // Use the full lead data returned from the backend
      updateLeadGlobal(res.data.data, leadId);
    },
    [updateLeadGlobal],
  );

  const updateBooking = useCallback(
    async (leadId, type, bookingId, payload) => {
      // Slot conflict check removed - multiple bookings per slot now allowed
      const res = await updateBookingApi(leadId, type, bookingId, payload);
      // Use the full lead data returned from the backend
      updateLeadGlobal(res.data.data, leadId);
    },
    [updateLeadGlobal],
  );

  const deleteBooking = useCallback(async (leadId, type, bookingId) => {
    await deleteBookingApi(leadId, type, bookingId);
    updateLeadGlobal(
      (prev) => ({
        ...prev,
        bookings: prev.bookings.filter((b) => b._id !== bookingId),
      }),
      leadId,
    );
  }, []);

  const addPayment = useCallback(
    async (id, payload) => {
      const res = await addPaymentApi(id, payload);
      updateLeadGlobal(
        (prev) => ({
          ...prev,
          payments: [...(prev.payments || []), res.data.data],
        }),
        id,
      );
    },
    [updateLeadGlobal],
  );

  const deletePayment = useCallback(
    async (id, paymentId) => {
      await deletePaymentApi(id, paymentId);
      updateLeadGlobal(
        (prev) => ({
          ...prev,
          payments: prev.payments.filter((p) => p._id !== paymentId),
        }),
        id,
      );
    },
    [updateLeadGlobal],
  );

  const uploadAttachments = useCallback(
    async (id, files) => {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const res = await addAttachmentApi(id, form);
      updateLeadGlobal(
        (prev) => ({
          ...prev,
          attachments: [...(prev.attachments || []), ...res.data.data],
        }),
        id,
      );
    },
    [updateLeadGlobal],
  );

  const deleteAttachment = useCallback(
    async (id, attachmentId) => {
      await deleteAttachmentApi(id, attachmentId);
      updateLeadGlobal(
        (prev) => ({
          ...prev,
          attachments: prev.attachments.filter((a) => a._id !== attachmentId),
        }),
        id,
      );
    },
    [updateLeadGlobal],
  );

  const addTask = useCallback(async (leadId, payload) => {
    const res = await addTaskApi(leadId, payload);

    updateLeadGlobal(
      (prev) => ({
        ...prev,
        tasks: [...(prev.tasks || []), res.data.data],
      }),
      leadId,
    );
  }, []);

  const updateTask = useCallback(async (leadId, taskId, payload) => {
    const res = await updateTaskApi(leadId, taskId, payload);

    updateLeadGlobal(
      (prev) => ({
        ...prev,
        tasks: prev.tasks.map((t) => (t._id === taskId ? res.data.data : t)),
      }),
      leadId,
    );
  }, []);

  const deleteTask = useCallback(async (leadId, taskId) => {
    await deleteTaskApi(leadId, taskId);

    updateLeadGlobal(
      (prev) => ({
        ...prev,
        tasks: prev.tasks.filter((t) => t._id !== taskId),
      }),
      leadId,
    );
  }, []);

  const filteredLeads = useMemo(() => {
    if (isLeadsPage)
      return leads.filter((l) => l.current_status?.type === "grade");
    if (isPipelinesPage)
      return leads.filter((l) => l.current_status?.type === "pipeline");
    return leads;
  }, [leads, isLeadsPage, isPipelinesPage]);

  const tabs = useMemo(() => {
    if (!settings) return ["all"];
    if (isLeadsPage && Array.isArray(settings.grade_status))
      return ["all", ...settings.grade_status];
    if (isPipelinesPage && Array.isArray(settings.pipeline_status))
      return ["all", ...settings.pipeline_status];
    return ["all"];
  }, [settings, isLeadsPage, isPipelinesPage]);

  const globalBookedSlots = useMemo(() => {
    const map = {};
    leads.forEach((lead) => {
      (lead.bookings || []).forEach((b) => {
        if (b.status === "Cancelled") return;
        if (!b.date || !b.slot) return;
        const date = b.date.slice(0, 10);
        const key = `${date}|${b.slot}|${b.type}`;
        map[key] = (map[key] || 0) + 1;
      });
    });
    return map;
  }, [leads]);

  const refreshLeads = useCallback(() => {
    setIsStale(true);
  }, []);

  const outletContext = useMemo(
    () => ({
      leads: filteredLeads,
      activeLead,
      clearActiveLead,
      loadLeadById,
      setLeads,
      updateLeadField,
      addBooking,
      updateBooking,
      deleteBooking,
      globalBookedSlots,
      addPayment,
      deletePayment,
      uploadAttachments,
      deleteAttachment,
      tabs,
      leadSource: settings?.lead_features?.sources || [],
      targetMinutes: settings?.maxSla ?? null,
      loading,
      allLeads: leads,

      tasks: activeLead?.tasks || [],
      addTask,
      updateTask,
      deleteTask,
      createLead,
      fetchComplete,
      refreshLeads,
    }),
    [
      filteredLeads,
      activeLead,
      clearActiveLead,
      setLeads,
      loadLeadById,
      updateLeadField,
      addBooking,
      updateBooking,
      deleteBooking,
      globalBookedSlots,
      addPayment,
      deletePayment,
      uploadAttachments,
      deleteAttachment,
      tabs,
      settings,
      loading,
      addTask,
      updateTask,
      deleteTask,
      createLead,
      refreshLeads,
    ],
  );

  return <Outlet context={outletContext} />;
}
